import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Award, TrendingUp, Users, Eye, Download, Share2, Loader2 } from "lucide-react";
import IssuerLayout from "@/components/IssuerLayout";
import Wizard from "@/components/Wizard";
import AppSumoReviewPrompt from "@/components/AppSumoReviewPrompt";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IssuerDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [usage, setUsage] = useState({
    certificates_issued: 0,
    is_paid: false,
    certificates_remaining: null
  });
  const [showWizard, setShowWizard] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('issuer_token');
    if (!token) {
      navigate("/issuer/login");
      return;
    }
    
    fetchStats();
    fetchUsage();
  }, []);
  
  // Separate effect to watch for wizard URL parameter
  useEffect(() => {
    if (searchParams.get('wizard') === 'true') {
      setShowWizard(true);
      // Remove the parameter from URL
      searchParams.delete('wizard');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams]);

  useEffect(() => {
    // Only run check after usage data has been loaded
    if (usage.certificates_issued === undefined) {
      return;
    }
    
    // Check if user has completed wizard (from backend)
    const checkWizardStatus = async () => {
      try {
        const token = localStorage.getItem('issuer_token');
        const response = await axios.get(`${API}/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const hasCompletedWizard = response.data.has_completed_wizard;
        
        // Show wizard for users with 0 certificates who haven't completed it
        if (!hasCompletedWizard && usage.certificates_issued === 0) {
          console.log('Showing wizard for new user with 0 certificates');
          setTimeout(() => setShowWizard(true), 800);
        }
      } catch (error) {
        console.error('Error checking wizard status:', error);
      }
    };
    
    checkWizardStatus();
  }, [usage.certificates_issued]);

  const fetchUsage = async () => {
    try {
      const token = localStorage.getItem('issuer_token');
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsage({
        certificates_issued: response.data.certificates_issued || 0,
        is_paid: response.data.is_paid || false,
        certificates_remaining: response.data.certificates_remaining
      });
    } catch (error) {
      console.error('Error fetching usage:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('issuer_token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <IssuerLayout>
        <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
          <Loader2 className="h-8 w-8 animate-spin text-slate-900" />
        </div>
      </IssuerLayout>
    );
  }

  const handleWizardComplete = () => {
    setShowWizard(false);
    localStorage.setItem('wizard_completed', 'true');
    fetchStats();
    fetchUsage();
  };

  const handleWizardSkip = () => {
    setShowWizard(false);
    localStorage.setItem('wizard_completed', 'true');
  };

  return (
    <IssuerLayout>
      {showWizard && (
        <Wizard onComplete={handleWizardComplete} onSkip={handleWizardSkip} />
      )}
      <AppSumoReviewPrompt />
      <div data-testid="dashboard-page">
        <div className="mb-8">
          <h1 className="text-4xl font-medium text-slate-900 mb-2" style={{fontFamily: 'Playfair Display'}}>Dashboard</h1>
          <p className="text-slate-600">Overview of your certificate activity</p>
        </div>

        {/* Quick Action: Restart Wizard */}
        {usage.certificates_issued > 0 && !showWizard && (
          <div className="mb-6 p-4 rounded-lg border border-slate-200 bg-slate-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900">Need help getting started?</p>
                <p className="text-sm text-slate-600 mt-1">Run the onboarding wizard again</p>
              </div>
              <button
                onClick={() => {
                  localStorage.removeItem('wizard_completed');
                  setShowWizard(true);
                }}
                className="px-4 py-2 bg-white text-slate-900 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors font-medium"
              >
                Restart Wizard
              </button>
            </div>
          </div>
        )}

        {/* Free Tier Usage Banner */}
        {!usage.is_paid && (
          <div className={`mb-6 p-4 rounded-lg border ${usage.certificates_remaining === 0 ? 'bg-red-50 border-red-200' : usage.certificates_remaining <= 2 ? 'bg-yellow-50 border-yellow-200' : 'bg-blue-50 border-blue-200'}`} data-testid="usage-banner">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900">
                  {usage.certificates_remaining === 0 ? (
                    '🚫 Free Tier Limit Reached'
                  ) : (
                    `📊 ${usage.certificates_issued} / 5 Free Certificates Used`
                  )}
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  {usage.certificates_remaining === 0 ? (
                    'Upgrade to issue unlimited certificates'
                  ) : (
                    `${usage.certificates_remaining} ${usage.certificates_remaining === 1 ? 'certificate' : 'certificates'} remaining`
                  )}
                </p>
              </div>
              {(usage.certificates_remaining === 0 || usage.certificates_remaining <= 2) && (
                <button
                  onClick={() => navigate('/pricing')}
                  className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium"
                >
                  Upgrade Now
                </button>
              )}
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="p-6 bg-white border border-slate-100 shadow-sm rounded-xl hover:border-slate-200 smooth-transition" data-testid="stat-certificates">
            <div className="flex items-center justify-between mb-4">
              <div className="h-12 w-12 bg-slate-900 rounded-lg flex items-center justify-center">
                <Award className="h-6 w-6 text-white" />
              </div>
              <TrendingUp className="h-5 w-5 text-green-500" />
            </div>
            <p className="text-sm text-slate-500 mb-1">Certificates Created</p>
            <p className="text-3xl font-bold text-slate-900">{stats?.certificates || 0}</p>
          </div>

          <div className="p-6 bg-white border border-slate-100 shadow-sm rounded-xl hover:border-slate-200 smooth-transition" data-testid="stat-recipients">
            <div className="flex items-center justify-between mb-4">
              <div className="h-12 w-12 bg-blue-600 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-white" />
              </div>
              <TrendingUp className="h-5 w-5 text-green-500" />
            </div>
            <p className="text-sm text-slate-500 mb-1">Recipients</p>
            <p className="text-3xl font-bold text-slate-900">{stats?.recipients || 0}</p>
          </div>

          <div className="p-6 bg-white border border-slate-100 shadow-sm rounded-xl hover:border-slate-200 smooth-transition" data-testid="stat-views">
            <div className="flex items-center justify-between mb-4">
              <div className="h-12 w-12 bg-amber-600 rounded-lg flex items-center justify-center">
                <Eye className="h-6 w-6 text-white" />
              </div>
            </div>
            <p className="text-sm text-slate-500 mb-1">Total Views</p>
            <p className="text-3xl font-bold text-slate-900">{stats?.views || 0}</p>
          </div>

          <div className="p-6 bg-white border border-slate-100 shadow-sm rounded-xl hover:border-slate-200 smooth-transition" data-testid="stat-downloads">
            <div className="flex items-center justify-between mb-4">
              <div className="h-12 w-12 bg-green-600 rounded-lg flex items-center justify-center">
                <Download className="h-6 w-6 text-white" />
              </div>
            </div>
            <p className="text-sm text-slate-500 mb-1">Total Downloads</p>
            <p className="text-3xl font-bold text-slate-900">{stats?.downloads || 0}</p>
          </div>

          <div className="p-6 bg-white border border-slate-100 shadow-sm rounded-xl hover:border-slate-200 smooth-transition" data-testid="stat-shares">
            <div className="flex items-center justify-between mb-4">
              <div className="h-12 w-12 bg-purple-600 rounded-lg flex items-center justify-center">
                <Share2 className="h-6 w-6 text-white" />
              </div>
            </div>
            <p className="text-sm text-slate-500 mb-1">LinkedIn Shares</p>
            <p className="text-3xl font-bold text-slate-900">{stats?.linkedin_shares || 0}</p>
          </div>
        </div>
      </div>
    </IssuerLayout>
  );
};

export default IssuerDashboard;
